import { c as _c } from "react/compiler-runtime";
import { AuthContext } from "@/components/auth/Auth";
import { UserFollowBtnSize } from "@/components/user/utils/UserFollowBtn";
import { ApiNotificationCause } from "@/generated/models/ApiNotificationCause";
import type { INotificationIdentityNic, INotificationIdentityRep } from "@/types/feed.types";
import Link from "next/link";
import { useContext, type ReactNode } from "react";
import NotificationsFollowBtn from "../NotificationsFollowBtn";
import NotificationHeader from "../subcomponents/NotificationHeader";
import NotificationTimestamp from "../subcomponents/NotificationTimestamp";
import { formatSignedNotificationNumber, getNotificationRatingColor, isNotificationNumber } from "../utils/notificationRatingUtils";
interface NotificationIdentityRatingProps {
  readonly notification: INotificationIdentityRep | INotificationIdentityNic;
}
function getTotalLabel({
  isRep,
  category
}: {
  readonly isRep: boolean;
  readonly category: string | undefined;
}): ReactNode {
  if (isRep && category) {
    return <>
        Total for{" "}
        <span className="tw-font-medium tw-text-iron-300">{category}</span>
      </>;
  }
  if (isRep) {
    return <>
        Total <span className="tw-font-medium tw-text-iron-300">REP</span>
      </>;
  }
  return <>
      Total <span className="tw-font-medium tw-text-iron-300">NIC</span>
    </>;
}
function NotificationRatingInlinePart(t0) {
  const $ = _c(16);
  const {
    label,
    value,
    separator: t1
  } = t0;
  const separator = t1 === undefined ? "bullet" : t1;
  if (!isNotificationNumber(value)) {
    return null;
  }
  const t2 = separator === "arrow" ? "tw-text-base tw-font-bold tw-text-iron-400" : "tw-text-xs tw-font-bold tw-text-iron-400";
  const t3 = separator === "arrow" ? "\u2192" : "\u2022";
  let t4;
  if ($[0] !== t2 || $[1] !== t3) {
    t4 = <span className={t2}>{t3}</span>;
    $[0] = t2;
    $[1] = t3;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== label) {
    t5 = <span className="tw-text-iron-400">{label}:</span>;
    $[3] = label;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== value) {
    t6 = getNotificationRatingColor(value);
    $[5] = value;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const t7 = `tw-font-medium tw-tabular-nums ${t6}`;
  let t8;
  if ($[7] !== value) {
    t8 = formatSignedNotificationNumber(value);
    $[7] = value;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== t7 || $[10] !== t8) {
    t9 = <span className={t7}>{t8}</span>;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  let t10;
  if ($[12] !== t4 || $[13] !== t5 || $[14] !== t9) {
    t10 = <span className="tw-inline-flex tw-items-center tw-gap-x-1 tw-whitespace-nowrap">{t4}{t5}{t9}</span>;
    $[12] = t4;
    $[13] = t5;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
}
export default function NotificationIdentityRating(t0) {
  const $ = _c(33);
  const {
    notification
  } = t0;
  const {
    connectedProfile
  } = useContext(AuthContext);
  const isRep = notification.cause === ApiNotificationCause.IdentityRep;
  const {
    amount,
    rater_rating,
    total
  } = notification.additional_context;
  const category = "category" in notification.additional_context ? notification.additional_context.category : undefined;
  const ratingLabel = isRep ? "REP" : "NIC";
  const profileHref = connectedProfile?.handle ? `/${connectedProfile.handle}` : null;
  let t1;
  if ($[0] !== ratingLabel) {
    t1 = <span className="tw-font-medium tw-text-iron-300">{ratingLabel}</span>;
    $[0] = ratingLabel;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = isRep ? null : " rating";
  let t3;
  if ($[2] !== category) {
    t3 = category ? <>{" "}for{" "}<span className="tw-font-medium tw-text-iron-300">{category}</span></> : null;
    $[2] = category;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = <>{t1}{t2}{t3}</>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const ratingPhrase = t4;
  let t5;
  if ($[8] !== category || $[9] !== isRep) {
    t5 = getTotalLabel({
      isRep,
      category
    });
    $[8] = category;
    $[9] = isRep;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const totalLabel = t5;
  let t6;
  if ($[11] !== notification.related_identity) {
    t6 = <NotificationsFollowBtn profile={notification.related_identity} size={UserFollowBtnSize.SMALL} />;
    $[11] = notification.related_identity;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== profileHref || $[14] !== ratingPhrase) {
    t7 = <span className="tw-text-base tw-font-normal tw-text-iron-400">updated your{" "}{profileHref ? <Link href={profileHref} className="tw-text-base tw-no-underline hover:tw-underline">{ratingPhrase}</Link> : ratingPhrase}</span>;
    $[13] = profileHref;
    $[14] = ratingPhrase;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== amount) {
    t8 = <NotificationRatingInlinePart label="Change" value={amount} />;
    $[16] = amount;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== rater_rating) {
    t9 = <NotificationRatingInlinePart label="New rating" value={rater_rating} separator="arrow" />;
    $[18] = rater_rating;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== total || $[21] !== totalLabel) {
    t10 = <NotificationRatingInlinePart label={totalLabel} value={total} />;
    $[20] = total;
    $[21] = totalLabel;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== notification.created_at) {
    t11 = <NotificationTimestamp createdAt={notification.created_at} className="tw-text-base" />;
    $[23] = notification.created_at;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== notification.related_identity || $[26] !== t10 || $[27] !== t11 || $[28] !== t6 || $[29] !== t7 || $[30] !== t8 || $[31] !== t9) {
    t12 = <div className="tw-w-full"><NotificationHeader author={notification.related_identity} authorClassName="tw-text-base" actions={t6}>{t7}{t8}{t9}{t10}{t11}</NotificationHeader></div>;
    $[25] = notification.related_identity;
    $[26] = t10;
    $[27] = t11;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
    $[31] = t9;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  return t12;
}